import { CompletionItemKind } from 'vscode-languageserver-types';
import * as lsp from 'vscode-languageserver';
import cp = require('child_process');
import { getBinPath, isPositionInString, goKeywords,
    getToolsEnvVars, guessPackageNameFromFile, goBuiltinTypes, byteOffsetAt, Thenable } from './util';
import { getImportablePackages } from './go-packages';

export class CompletionItem {
    public detail?: string;
    public documentation?: string;
    public sortText?: string;
    public filterText?: string;
    public insertText?: string;
    public insertTextFormat?: lsp.InsertTextFormat;
    public textEdit?: lsp.TextEdit;
    public additionalTextEdits?: lsp.TextEdit[];
    public commitCharacters?: string[];
    public command?: lsp.Command;
    public data?: any;

    constructor(public label: string, public kind?: lsp.CompletionItemKind) { }
}

export namespace InsertTextFormat {
    export const PlainText = 1;
    export const Snippet = 2;
}

function vscodeKindFromGoCodeClass(kind: string): CompletionItemKind {
    switch (kind) {
        case 'const':
        case 'package':
        case 'type':
            return CompletionItemKind.Keyword;
        case 'func':
            return CompletionItemKind.Function;
        case 'var':
            return CompletionItemKind.Field;
        case 'import':
            return CompletionItemKind.Module;
    }
    return CompletionItemKind.Property; // TODO@EG additional mappings needed?
}

interface GoCodeSuggestion {
    class: string;
    name: string;
    type: string;
}

export class GoCompletionItemProvider {

    private pkgsList = new Map<string, string>();

    public provideCompletionItems(document, position): Thenable<any[]> {
        return this.provideCompletionItemsInternal(document, position);
    }

    public provideCompletionItemsInternal(document, position): Thenable<any[]> {
        return this.ensureGoCodeConfigured(document.fileName).then(() => {
            return new Promise<CompletionItem[]>((resolve, reject) => {
                let filename = document.fileName;
                let lineText = document.lineAt(position.line).text;
                let lineTillCurrentPosition = lineText.substr(0, position.character);

                if (lineText.match(/^\s*\/\//)) {
                    return resolve([]);
                }

                let inString = isPositionInString(document, position);
                if (!inString && lineTillCurrentPosition.endsWith('\"')) {
                    return resolve([]);
                }

                // get current word
                let wordAtPosition = document.getWordRangeAtPosition(position);
                let currentWord = '';
                if (wordAtPosition && wordAtPosition.start.character < position.character) {
                    let word = document.getText(wordAtPosition);
                    currentWord = word.substr(0, position.character - wordAtPosition.start.character);
                }

                if (currentWord.match(/^\d+$/)) {
                    return resolve([]);
                }

                let offset = byteOffsetAt(document, position);
                let inputText = document.getText();

                return this.runGoCode(document, filename, inputText, offset, inString, position, lineText, currentWord, false).then(suggestions => {
                    // gocode does not suggest keywords, so we have to do it
                    if (currentWord.length > 0) {
                        goKeywords.forEach(keyword => {
                            if (keyword.startsWith(currentWord)) {
                                const keywordConst = new CompletionItem(keyword, CompletionItemKind.Keyword);
                                keywordConst.detail = keywordConst.detail ? keywordConst.detail : '';
                                suggestions.push(keywordConst);
                            }
                        });
                    }

                    resolve(suggestions);
                });
            });
        });
    }

    private runGoCode(document, filename, inputText, offset, inString, position, lineText, currentWord, includeUnimportedPkgs): Thenable<any[]> {
        return new Promise<CompletionItem[]>((resolve, reject) => {
            let gocode = getBinPath('gocode');

            // Unset GOOS and GOARCH for the `gocode` process to ensure that GOHOSTOS and GOHOSTARCH
            // are used as the target operating system and architecture. `gocode` is unable to provide
            // autocompletion when the Go environment is configured for cross compilation.
            let env = Object.assign({}, getToolsEnvVars(), { GOOS: '', GOARCH: '' });
            let stdout = '';
            let stderr = '';
            // Spawn `gocode` process
            let p = cp.spawn(gocode, ['-f=json', 'autocomplete', filename, '' + offset], { env });
            p.stdout.on('data', data => stdout += data);
            p.stderr.on('data', data => stderr += data);
            p.on('error', err => {
                if (err && (<any>err).code === 'ENOENT') {
                    return reject();
                }
                return reject(err);
            });
            p.on('close', code => {
                try {
                    if (code !== 0) {
                        return reject(stderr);
                    }
                    let results = <[number, GoCodeSuggestion[]]>JSON.parse(stdout.toString());
                    let suggestions = [];
                    let suggestionSet = new Set<string>();

                    let wordAtPosition = document.getWordRangeAtPosition(position);

                    if (results[1]) {
                        for (let suggest of results[1]) {
                            if (inString && suggest.class !== 'import') { continue; }
                            let item = new CompletionItem(suggest.name);
                            item.kind = vscodeKindFromGoCodeClass(suggest.class);
                            item.detail = suggest.type;

                            if (wordAtPosition && wordAtPosition.start.character === 0 &&
                                suggest.class === 'type' && !goBuiltinTypes.has(suggest.name)) {
                                let auxItem = new CompletionItem(suggest.name + ' method', CompletionItemKind.Snippet);
                                auxItem.label = 'func (*' + suggest.name + ')';
                                auxItem.filterText = suggest.name;
                                auxItem.detail = 'Method snippet';
                                auxItem.sortText = 'b';
                                let prefix = 'func (' + suggest.name[0].toLowerCase() + ' *' + suggest.name + ')';
                                let snippet = prefix + ' ${1:methodName}(${2}) ${3} \{\n\t$0\n\}';
                                // [TypeFox]
                                // auxItem.insertText = new vscode.SnippetString(snippet);
                                auxItem.insertText = snippet;
                                auxItem.insertTextFormat = InsertTextFormat.Snippet;
                                suggestions.push(auxItem);
                            }

                            // Add same sortText to all suggestions from gocode so that they appear before the unimported packages
                            item.sortText = 'a';
                            suggestions.push(item);
                            suggestionSet.add(item.label);
                        }
                    }

                    // Add importable packages matching currentword to suggestions
                    let importablePkgs = includeUnimportedPkgs ? this.getMatchingPackages(currentWord, suggestionSet) : [];
                    suggestions = suggestions.concat(importablePkgs);

                    // 'Smart Snippet' for package clause
                    // TODO: Factor this out into a general mechanism
                    if (!inputText.match(/package\s+(\w+)/)) {
                        return guessPackageNameFromFile(filename).then((pkgNames: string[]) => {
                            pkgNames.forEach(pkgName => {
                                let packageItem = new CompletionItem('package ' + pkgName);
                                packageItem.kind = CompletionItemKind.Snippet;
                                packageItem.insertText = 'package ' + pkgName + '\r\n\r\n';
                                suggestions.push(packageItem);
                            });
                            resolve(suggestions);
                        }, () => resolve(suggestions));
                    }

                    resolve(suggestions);
                } catch (e) {
                    reject(e);
                }
            });
            p.stdin.end(inputText);
        });
    }
    // TODO: Shouldn't lib-path also be set?
    private ensureGoCodeConfigured(fileName): Thenable<void> {
        let setPkgsList = getImportablePackages(fileName, true).then(pkgMap => this.pkgsList = pkgMap);

        let setGocodeProps = new Promise<void>((resolve, reject) => {
            let gocode = getBinPath('gocode');
            let env = getToolsEnvVars();

            cp.execFile(gocode, ['set'], { env }, (err, stdout, stderr) => {
                const existingOptions = stdout.split(/\r\n|\n/);
                const optionsToSet: string[][] = [];
                const setOption = () => {
                    const [name, value] = optionsToSet.pop();
                    cp.execFile(gocode, ['set', name, value], { env }, (err, stdout, stderr) => {
                        if (optionsToSet.length) {
                            setOption();
                        } else {
                            resolve();
                        }
                    });
                };

                if (existingOptions.indexOf('propose-builtins true') === -1) {
                    optionsToSet.push(['propose-builtins', 'true']);
                }
                if (!optionsToSet.length) {
                    return resolve();
                }

                setOption();
            });
        });

        return Promise.all([setPkgsList, setGocodeProps]).then(() => {
            return;
        });
    }

    // Return importable packages that match given word as Completion Items
    private getMatchingPackages(word: string, suggestionSet: Set<string>): CompletionItem[] {
        if (!word) {return []; }
        let completionItems = [];

        this.pkgsList.forEach((pkgName: string, pkgPath: string) => {
            if (pkgName.startsWith(word) && !suggestionSet.has(pkgName)) {

                let item = new CompletionItem(pkgName, CompletionItemKind.Keyword);
                item.detail = pkgPath;
                item.documentation = 'Imports the package';
                item.insertText = pkgName;
                item.command = {
                    title: 'Import Package',
                    command: 'go.import.add',
                    arguments: [pkgPath],
                };
                // Add same sortText to the unimported packages so that they appear after the suggestions from gocode
                const isStandardPackage = !item.detail.includes('.');
                item.sortText = isStandardPackage ? 'za' : 'zb';
                completionItems.push(item);
            }
        });
        return completionItems;
    }

    // Given a line ending with dot, return the word preceeding the dot if it is a package name that can be imported
    private getPackagePathFromLine(line: string): string {
        let pattern = /(\w+)\.$/g;
        let wordmatches = pattern.exec(line);
        if (!wordmatches) {
            return;
        }

        let [_, pkgNameFromWord] = wordmatches;
        // Word is isolated. Now check pkgsList for a match
        let matchingPackages = [];
        this.pkgsList.forEach((pkgName: string, pkgPath: string) => {
            if (pkgNameFromWord === pkgName) {
                matchingPackages.push(pkgPath);
            }
        });

        if (matchingPackages && matchingPackages.length === 1) {
            return matchingPackages[0];
        }
    }
}

